import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Badge, Button, Spinner } from '@agion/ui'
import { KeyRound, RefreshCw } from 'lucide-react'
import { rolloutErrorText } from './rollout_refusal'
import { getManagedChatKeyHealth, type ManagedChatKeyHealth, type ManagedChatKeysHealth } from './infra_managed_chat_rollout.service'

type KeyState = 'active' | 'expiring' | 'expired' | 'disabled' | 'unverified' | 'unchecked' | 'unavailable' | 'unknown'
type BadgeVariant = 'success' | 'warning' | 'error' | 'default'

const knownStates: ReadonlySet<string> = new Set(['active', 'expired', 'disabled', 'unverified', 'unchecked', 'unavailable'])
const stateBadges: Readonly<Record<KeyState, BadgeVariant>> = {
  active: 'success',
  expiring: 'warning',
  expired: 'error',
  disabled: 'error',
  unverified: 'warning',
  unchecked: 'default',
  unavailable: 'default',
  unknown: 'default',
}
const dateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }
const dateTimeOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }

/** The state the card shows: the server's status, or expiring when it warns about an active key. */
function keyState(health: ManagedChatKeyHealth): KeyState {
  if (health.status === 'active' && health.expiryWarning) return 'expiring'
  return knownStates.has(health.status) ? health.status as KeyState : 'unknown'
}

function KeyRow({ health }: { health: ManagedChatKeyHealth }) {
  const { t, i18n } = useTranslation('pages')
  const state = keyState(health)
  const format = (value: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(i18n.language, options).format(new Date(value))
  // A key id is set only once Cloudflare has answered a check; with it, no
  // expiry means the key has none.
  const expiry = health.expiresOn
    ? format(health.expiresOn, dateOptions)
    : t(health.keyId ? 'rolloutControls.managedChat.keys.noExpiry' : 'rolloutControls.managedChat.keys.notKnown')
  const prefix = 'rolloutControls.managedChat.keys'
  return (
    <li className="p-4" data-testid={`managed-chat-key-${health.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {t(`${prefix}.names.${health.key}`, { defaultValue: health.key })}
        </span>
        <Badge variant={stateBadges[state]}>
          {t(`${prefix}.states.${state}.label`, { status: health.status })}
        </Badge>
      </div>
      <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">
        {t(`${prefix}.states.${state}.action`, { date: health.expiresOn ? format(health.expiresOn, dateOptions) : '' })}
      </p>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.keyId`)}</dt>
          <dd className="mt-1 break-all font-mono text-xs">{health.keyId || t(`${prefix}.notKnown`)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.expires`)}</dt>
          <dd className="mt-1">{expiry}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.lastChecked`)}</dt>
          <dd className="mt-1">{health.lastCheckedAt ? format(health.lastCheckedAt, dateTimeOptions) : t(`${prefix}.neverChecked`)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-slate-500">{t(`${prefix}.lastRefusal`)}</dt>
          <dd className="mt-1">
            {health.lastRefusalCode ? (
              <>
                <span className="font-mono text-xs">{health.lastRefusalCode}</span>
                {health.lastRefusalAt && (
                  <span className="block text-xs text-slate-500">{format(health.lastRefusalAt, dateTimeOptions)}</span>
                )}
              </>
            ) : t(`${prefix}.noRefusal`)}
          </dd>
        </div>
      </dl>
    </li>
  )
}

/**
 * Health of Agion's two Cloudflare keys of managed chat addresses (ADR-003
 * G7, PLAN-20261015 slice 15): each key's status, Cloudflare key id, expiry,
 * last check and last refusal code, and the expiry warning the server
 * raises from the catalogued number of days. The keys are the platform's,
 * so the panel reads the same answer for every organisation.
 */
export function ManagedChatKeyHealthPanel() {
  const { t } = useTranslation('pages')
  const [health, setHealth] = useState<ManagedChatKeysHealth | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const generation = useRef(0)

  const load = useCallback(async () => {
    const current = ++generation.current
    setIsLoading(true)
    setError(null)
    try {
      const answer = await getManagedChatKeyHealth()
      if (current !== generation.current) return
      setHealth(answer)
    } catch (loadError) {
      if (current !== generation.current) return
      setHealth(null)
      setError(rolloutErrorText(loadError, t, 'rolloutControls.managedChat.keys.loadError'))
    } finally {
      if (current === generation.current) setIsLoading(false)
    }
  }, [t])

  const invalidate = useCallback(() => {
    generation.current++
  }, [])

  useEffect(() => {
    void load()
    return invalidate
  }, [invalidate, load])

  const prefix = 'rolloutControls.managedChat.keys'
  return (
    <section className="space-y-3" aria-labelledby="managed-chat-keys-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div id="managed-chat-keys-title" className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
            <KeyRound className="h-4 w-4" aria-hidden="true" />
            {t(`${prefix}.title`)}
          </div>
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{t(`${prefix}.description`)}</p>
        </div>
        <Button variant="secondary" size="sm" disabled={isLoading} onClick={() => void load()}>
          <RefreshCw className="mr-1 h-4 w-4" />{t(`${prefix}.refresh`)}
        </Button>
      </div>
      {isLoading && !health ? (
        <div className="flex items-center justify-center py-6"><Spinner /></div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : health ? (
        <>
          {!health.available ? (
            <Alert variant="warning">
              {t(`${prefix}.notSetUp`, { names: health.missingConfiguration.join(', ') || t(`${prefix}.notKnown`) })}
            </Alert>
          ) : !health.usable && (
            <Alert variant="error">{t(`${prefix}.notUsable`)}</Alert>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t(`${prefix}.warningDays`, { days: health.expiryWarningDays })}
          </p>
          <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
            {health.keys.map((key) => <KeyRow key={key.key} health={key} />)}
          </ol>
        </>
      ) : null}
    </section>
  )
}
